/**
 * LES DONNÉES DES RAPPORTS — une seule fois, pour les deux affichages.
 *
 * CE QUI SE PASSAIT. « MobileReports » recalculait tout de son côté : la
 * période, les mois, le chiffre d'affaires, le non encaissé, les statistiques
 * clients. Ses calculs étaient une COPIE de ceux du bureau, faite avant la
 * correction des avoirs — et jamais reprise depuis. Sur mobile, les avoirs
 * étaient donc toujours ADDITIONNÉS au chiffre d'affaires : Eco Delivery Urban
 * y affichait encore le chiffre faux, longtemps après que le bureau eut été
 * corrigé.
 *
 * Deux copies d'une même règle divergent toujours. La question n'est pas si,
 * mais quand. Il n'y en a donc plus qu'une : ce module. Le bureau et le mobile
 * reçoivent le MÊME objet, et ne diffèrent que par ce qu'ils en dessinent.
 *
 * Ce module ne formate rien. Il ne rend ni libellé de mois, ni montant en
 * euros : ces choix-là appartiennent à l'écran, et c'est précisément ce qui
 * autorise les deux présentations à différer sans que les nombres bougent. Un
 * point mensuel porte sa clé « 2026-08 » ; au bureau de l'écrire « août 2026 »
 * et au mobile « août 26 ».
 */

import {
  estAvoir,
  entreDansLeCA,
  montantTTCSigne,
  compteursAvoirs,
  serieDuDocument,
  imputer,
  entreDansLActivite,
  estEmise,
  type CompteursAvoirs,
} from '@/lib/avoirs';
import { anneeGlissante, moisDeLaPeriode, completerLesMois, cleDuMois } from '@/lib/mois';

export type PeriodeRapport = 'month' | '3months' | 'custom' | 'total' | 'rolling12';

export interface BornesPeriode {
  debut: Date;
  fin: Date;
}

/**
 * Les bornes d'une période, calculées ICI et nulle part ailleurs.
 *
 * C'est la même fonction qui borne le filtrage des documents et la requête des
 * métriques serveur, au bureau comme au mobile. Quatre copies de ce calcul
 * existaient auparavant, dont deux qui ne connaissaient pas l'année glissante.
 */
export function bornesDeLaPeriode(
  periode: PeriodeRapport,
  maintenant: Date,
  customStart?: string | null,
  customEnd?: string | null
): BornesPeriode {
  const debutDuMois = new Date(maintenant.getFullYear(), maintenant.getMonth(), 1, 0, 0, 0, 0);
  const finDuMois = new Date(maintenant.getFullYear(), maintenant.getMonth() + 1, 0, 23, 59, 59, 999);

  switch (periode) {
    case 'rolling12':
      return anneeGlissante(maintenant);

    case 'total':
      return { debut: new Date(2000, 0, 1, 0, 0, 0, 0), fin: finDuMois };

    case '3months': {
      const debut = new Date(maintenant.getFullYear(), maintenant.getMonth() - 3, maintenant.getDate(), 0, 0, 0, 0);
      return { debut, fin: finDuMois };
    }

    case 'month':
      return { debut: debutDuMois, fin: finDuMois };

    case 'custom':
    default: {
      if (!customStart || !customEnd) return { debut: debutDuMois, fin: finDuMois };
      const debut = new Date(`${customStart}T00:00:00`);
      const fin = new Date(`${customEnd}T23:59:59.999`);
      if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime())) {
        return { debut: debutDuMois, fin: finDuMois };
      }
      return { debut, fin };
    }
  }
}

/**
 * UN MOIS DU GRAPHIQUE — deux séries, deux dates, et plus d'avoirs dessinés.
 *
 * Signalé par Amine le 18 septembre 2026, en deux phrases :
 *
 *  - « N'affiche pas les avoirs émis dans les rapports, ça fait une barre
 *    orange vers le bas. Et n'intègre pas les factures émises passées en
 *    avoir, ça pollue pour rien. »
 *  - « En août j'ai encaissé le 31.08.26 la facture 20231268 (marquée payée à
 *    cette date). Donc 24 144 € devrait être dans le chiffre d'affaires. »
 *
 * D'où les deux règles :
 *
 *  - « factures » est L'ARGENT REÇU dans le mois, daté par le RÈGLEMENT — la
 *    même règle que « encaissementsDeLaPeriode », mois par mois. La 20231268,
 *    émise le 30 juillet et réglée le 31 août, se lit en août.
 *  - « estimation » est le SOLDE des factures émises dans le mois et non
 *    réglées, avoirs liés déduits. Une facture intégralement annulée par avoir
 *    vaut 0 et disparaît ; une facture partiellement avoirée montre ce qui
 *    reste dû. Les avoirs eux-mêmes ne sont plus une série dessinée.
 *
 * « avoirs » reste calculé — au mois de leur émission, en négatif — pour qui
 * voudrait le lire, mais aucun des deux écrans ne le dessine plus.
 */
export interface PointMensuel {
  /** « 2026-08 ». Le libellé appartient à l'écran. */
  cle: string;
  /** L'argent reçu dans le mois, daté par le règlement. */
  factures: number;
  /** Le solde des factures émises dans le mois et non réglées, avoirs liés déduits. */
  estimation: number;
  /** Les avoirs, négatifs, au mois de leur émission. Calculé, plus dessiné. */
  avoirs: number;
  devis: number;
}

export interface StatClient {
  id: string;
  revenue: number;
  count: number;
  draftRevenue: number;
  draftCount: number;
}

export interface DonneesRapport {
  periode: BornesPeriode;
  /** Les documents retenus par la période. */
  factures: any[];
  devis: any[];
  /**
   * Parmi ce que la période a FACTURÉ, ce qui est déjà réglé — le solde des
   * factures « Payée » émises dans la période, avoirs déduits. Il se lit avec
   * « nonEncaisse » : ensemble, ils recomposent ce que la période a facturé.
   */
  caEncaisse: number;
  /** L'argent REÇU pendant la période, daté par le règlement. */
  encaissements: number;
  nombreEncaissements: number;
  /** Facturé net : le solde de tout ce qui entre dans le CA. */
  caFactureNet: number;
  /** Reste à encaisser : le SOLDE des factures émises non réglées. */
  nonEncaisse: number;
  nonEncaisseCount: number;
  /** En attente de règlement : le SOLDE des « Envoyée » et « Retard ». */
  enAttente: number;
  enAttenteCount: number;
  /** Les avoirs qu'aucune facture de la période n'absorbe. */
  avoirsNonImputes: number;
  avoirs: CompteursAvoirs;
  volumeDevis: number;
  nombreDevis: number;
  tauxConversion: number;
  clientsActifs: number;
  nombreFactures: number;
  nombreFacturesPayees: number;
  mensuel: PointMensuel[];
  clients: StatClient[];
  produits: any[];
  /** Les quatre nombres, le détail par mois, et une ligne par facture. */
  tableauDeBord: TableauDeBord;
}

export interface EntreeRapport {
  factures: any[];
  devis: any[];
  periode: PeriodeRapport;
  maintenant: Date;
  customStart?: string | null;
  customEnd?: string | null;
  /** « topClients » / « topProducts » calculés côté serveur, s'ils sont là. */
  metriquesServeur?: { topClients?: any[]; topProducts?: any[] } | null;
  /** Les fiches clients, pour nommer les factures du détail. */
  clientsConnus?: { id: string; nom: string }[];
}

function arrondir(montant: number): number {
  return Math.round(montant * 100) / 100;
}

function dansLaPeriode(dateISO: string | undefined | null, bornes: BornesPeriode): boolean {
  if (!dateISO) return false;
  const date = new Date(dateISO);
  if (Number.isNaN(date.getTime())) return false;
  return date >= bornes.debut && date <= bornes.fin;
}

/**
 * CE QUI EST RÉELLEMENT ENTRÉ — l'argent reçu pendant la période.
 *
 * À ne pas confondre avec « caEncaisse », qui répond à une autre question :
 * parmi ce que la période a FACTURÉ, qu'est-ce qui est déjà réglé ? Les deux
 * sont légitimes, et ne donnent pas le même nombre. Une facture d'avril réglée
 * en juin compte ici en JUIN, et là-bas en AVRIL.
 *
 * Un encaissement se date par son RÈGLEMENT, jamais par son émission. C'est la
 * seule lecture qui répond à « combien ai-je touché ces trois derniers mois ».
 *
 * Un avoir « Payée » est un remboursement SORTI : il vient en déduction, par
 * son signe, au mois où il a été remboursé.
 */
export function encaissementsDeLaPeriode(
  documents: any[],
  bornes: BornesPeriode
): { montant: number; nombre: number } {
  const regles = (documents || [])
    .filter(entreDansLActivite)
    .filter((d) => d?.statut === 'Payée')
    .filter((d) => dansLaPeriode(d?.datePaiement, bornes));

  return {
    montant: arrondir(regles.reduce((s, d) => s + montantTTCSigne(d), 0)),
    nombre: regles.filter((d) => !estAvoir(d)).length,
  };
}

/**
 * Tout ce qu'un écran de rapports a besoin de savoir, pour une société et une
 * période. Le bureau et le mobile appellent CECI, et rien d'autre.
 */
export function construireRapport(entree: EntreeRapport): DonneesRapport {
  const periode = bornesDeLaPeriode(
    entree.periode,
    entree.maintenant,
    entree.customStart,
    entree.customEnd
  );

  // « entreDansLActivite » AVANT tout le reste : ce qui est archivé est sorti
  // de l'activité et n'entre dans aucun calcul — ni chiffre d'affaires, ni
  // créance, ni graphique, ni statistique. Filtré ici, une seule fois, il ne
  // peut pas se glisser dans un agrégat plus bas.
  const factures = (entree.factures || [])
    .filter(entreDansLActivite)
    .filter((f) => dansLaPeriode(f?.dateEmission, periode));
  // Les devis portent « Archivé » au masculin — même règle, même sortie.
  const devis = (entree.devis || [])
    .filter((d) => d?.statut !== 'Archivé' && d?.statut !== 'Archivée')
    .filter((d) => dansLaPeriode(d?.dateEmission, periode));

  // --- Les avoirs, rattachés à la facture qu'ils annulent ---
  //
  // C'est ce rattachement qui fait la différence entre « ce qui a été facturé »
  // et « ce qui reste dû ». Une facture intégralement avoirée n'est plus une
  // créance : personne ne la paiera, et l'afficher parmi les montants à
  // encaisser présente comme dû ce qui est déjà compensé.
  const imputation = imputer(factures);
  const soldeDe = (facture: any): number => imputation.solde(facture);

  /**
   * Les avoirs qu'aucune facture de la période n'a absorbés : non rattachés,
   * rattachés à une facture hors période, ou dépassant son montant.
   *
   * Sans eux, un avoir orphelin disparaîtrait purement et simplement du calcul.
   * Avec eux, chaque euro d'avoir est retranché UNE fois : soit dans le solde
   * de sa facture, soit ici. Jamais deux fois, jamais zéro.
   */
  const avoirsNonImputes = arrondir(
    factures
      .filter((f) => estAvoir(f) && entreDansLeCA(f))
      .reduce((somme, avoir) => somme + imputation.partNonImputee(avoir), 0)
  );

  // --- Les indicateurs, en SOLDES ---
  //
  // « Encaissé », « en attente » et « non encaissé » comptent ce qui reste dû,
  // facture par facture. Les avoirs y sont donc DÉJÀ déduits, par le solde de
  // la facture qu'ils visent : les retrancher une seconde fois en tant que
  // documents les compterait deux fois.
  const facturesPayees = factures.filter(
    (f) => !estAvoir(f) && f.statut === 'Payée' && soldeDe(f) > 0
  );
  const caEncaisse = arrondir(
    factures
      .filter((f) => !estAvoir(f) && f.statut === 'Payée')
      .reduce((s, f) => s + soldeDe(f), 0)
  );

  // Le non encaissé est une ESTIMATION : il inclut les brouillons, qui ne sont
  // pourtant pas des créances. « Téléchargée » y manquait — une facture remise
  // au client sans passer par l'envoi depuis l'application est bien émise.
  const nonEncaissees = factures.filter(
    (f) => !estAvoir(f) && (f.statut === 'Brouillon' || estEmise(f)) && soldeDe(f) > 0
  );
  const nonEncaisse = arrondir(nonEncaissees.reduce((s, f) => s + soldeDe(f), 0));

  // En attente de règlement : les factures ÉMISES et non soldées, brouillons
  // exclus. « Téléchargée » comprise.
  const enAttentes = factures.filter((f) => !estAvoir(f) && estEmise(f) && soldeDe(f) > 0);
  const enAttente = arrondir(enAttentes.reduce((s, f) => s + soldeDe(f), 0));

  // Le CA net suit la même règle, et retranche en plus les avoirs qu'aucune
  // facture n'a absorbés.
  const caFactureNet = arrondir(
    factures
      .filter((f) => !estAvoir(f) && entreDansLeCA(f))
      .reduce((s, f) => s + soldeDe(f), 0) - avoirsNonImputes
  );

  const avoirs = compteursAvoirs(factures);

  const volumeDevis = arrondir(devis.reduce((s, d) => s + (Number(d.totalTTC) || 0), 0));
  const devisAcceptes = devis.filter((d) => d.statut === 'Accepté' || d.statut === 'Converti');
  const tauxConversion = devis.length > 0 ? (devisAcceptes.length / devis.length) * 100 : 0;

  // --- L'axe mensuel ---
  //
  // Semé par la PÉRIODE : les douze mois d'une année glissante existent même
  // sur une société qui n'a facturé qu'une fois, et les mois vides valent zéro.
  const points = new Map<string, PointMensuel>();
  const cleDe = (dateISO: string | undefined | null) => {
    if (!dateISO) return 'unknown';
    const date = new Date(dateISO);
    return Number.isNaN(date.getTime()) ? 'unknown' : cleDuMois(date);
  };

  const clesObservees = [...factures, ...devis].map((d) => cleDe(d.dateEmission));
  const clesDeLaPeriode = moisDeLaPeriode(periode.debut, periode.fin);
  for (const cle of completerLesMois([...clesDeLaPeriode, ...clesObservees])) {
    points.set(cle, { cle, factures: 0, estimation: 0, avoirs: 0, devis: 0 });
  }

  // Ce qui a été FACTURÉ et reste dû, au mois de l'émission — en solde, pour
  // qu'une facture annulée par avoir ne dessine rien. Les avoirs vont dans
  // leur propre série, que personne ne dessine plus.
  for (const facture of factures) {
    const point = points.get(cleDe(facture.dateEmission));
    if (!point) continue;
    const serie = serieDuDocument(facture);
    if (serie === 'avoirs') {
      point.avoirs = arrondir(point.avoirs + montantTTCSigne(facture));
    } else if (serie === 'estimation') {
      point.estimation = arrondir(point.estimation + soldeDe(facture));
    }
  }
  // Ce qui a été REÇU, au mois du règlement — sur TOUS les documents de la
  // société : une facture émise avant la période mais réglée dedans compte.
  for (const document of (entree.factures || []).filter(entreDansLActivite)) {
    if (document?.statut !== 'Payée') continue;
    const point = points.get(cleDe(document.datePaiement));
    if (!point) continue;
    point.factures = arrondir(point.factures + montantTTCSigne(document));
  }
  for (const d of devis) {
    const point = points.get(cleDe(d.dateEmission));
    if (point) point.devis = arrondir(point.devis + (Number(d.totalTTC) || 0));
  }

  const mensuel = [...points.values()].sort((a, b) => a.cle.localeCompare(b.cle));

  // --- Clients ---
  //
  // Le serveur fait foi quand il a répondu : il voit les lignes de facture, que
  // le client n'a pas. À défaut, le même calcul, signé.
  let clients: StatClient[];
  if (entree.metriquesServeur?.topClients) {
    clients = entree.metriquesServeur.topClients.map((c: any) => ({
      id: c.id,
      revenue: c.revenue,
      count: c.count,
      draftRevenue: c.draftRevenue,
      draftCount: c.draftCount,
    }));
  } else {
    const stats = new Map<string, StatClient>();
    for (const facture of factures) {
      if (!facture.clientId) continue;
      const stat = stats.get(facture.clientId)
        || { id: facture.clientId, revenue: 0, count: 0, draftRevenue: 0, draftCount: 0 };
      if (facture.statut === 'Payée') {
        stat.revenue = arrondir(stat.revenue + montantTTCSigne(facture));
        stat.count += 1;
      } else if (facture.statut === 'Brouillon') {
        stat.draftRevenue = arrondir(stat.draftRevenue + montantTTCSigne(facture));
        stat.draftCount += 1;
      }
      stats.set(facture.clientId, stat);
    }
    clients = [...stats.values()]
      .filter((s) => s.revenue !== 0 || s.draftRevenue !== 0)
      .sort((a, b) => (b.revenue + b.draftRevenue) - (a.revenue + a.draftRevenue))
      .slice(0, 10);
  }

  // Daté par le RÈGLEMENT, et donc calculé sur TOUS les documents de la
  // société — pas sur ceux que la période a émis.
  const encaissements = encaissementsDeLaPeriode(entree.factures || [], periode);

  return {
    periode,
    factures,
    devis,
    caEncaisse,
    encaissements: encaissements.montant,
    nombreEncaissements: encaissements.nombre,
    caFactureNet,
    nonEncaisse,
    nonEncaisseCount: nonEncaissees.length,
    enAttente,
    enAttenteCount: enAttentes.length,
    avoirsNonImputes,
    avoirs,
    volumeDevis,
    nombreDevis: devis.length,
    tauxConversion,
    clientsActifs: new Set([...factures, ...devis].map((d) => d.clientId).filter(Boolean)).size,
    nombreFactures: factures.length,
    nombreFacturesPayees: facturesPayees.length,
    mensuel,
    clients,
    produits: entree.metriquesServeur?.topProducts || [],
    tableauDeBord: construireTableauDeBord(entree.factures || [], periode, entree.maintenant, entree.clientsConnus, entree.devis || []),
  };
}

// ===========================================================================
// LE TABLEAU DE BORD DE FACTURATION — quatre questions, quatre nombres.
// ===========================================================================
//
// Amine, 19 septembre 2026 : « le dashboard doit permettre de comprendre
// immédiatement combien a été facturé, combien a réellement été encaissé,
// combien reste à encaisser, combien est encore en brouillon ».
//
// CE QUI RENDAIT L'ANCIEN ÉCRAN ILLISIBLE. Il n'y avait pas de « facturé » :
// la première tuile disait le réglé, et son sous-titre « non encaissé »
// mélangeait les brouillons (77 316 €) aux factures émises non réglées
// (22 644 €). Le graphique faisait de même. On lisait 99 960 € « non
// encaissés » là où 22 644 € seulement étaient dus.
//
// LES QUATRE RÈGLES, et la date de chacune :
//
//  - FACTURÉ : le solde TTC des factures ÉMISES (Envoyée, Téléchargée, Retard,
//    Payée), avoirs liés déduits, au mois de leur ÉMISSION. Une facture
//    annulée par avoir vaut 0 ; une « Annulée » n'existe plus ; un brouillon
//    n'est pas du chiffre d'affaires.
//  - ENCAISSÉ : les règlements, au mois de leur RÈGLEMENT. Quand la facture
//    porte des règlements enregistrés (table « Paiement »), ce sont eux —
//    partiels compris. Sinon, une facture « Payée » est réglée pour son solde
//    à sa date de paiement. Un avoir remboursé se retranche.
//  - RESTE À ENCAISSER : solde moins encaissé, facture par facture, au mois
//    de l'ÉMISSION. Une facture « Payée » ne doit plus rien.
//  - BROUILLONS : le TTC des brouillons, au mois de leur date.
//
// D'où l'identité, sur l'ensemble des factures : FACTURÉ = ENCAISSÉ + RESTE.
// Elle ne tient qu'en totaux : par mois, un règlement reçu en août peut
// concerner une facture émise en juillet — c'est normal, et c'est dit.

export interface Reglement {
  date: string;
  montant: number;
}

/** Un règlement enregistré, tel que la projection peut le porter. */
function reglementsEnregistres(doc: any): Reglement[] {
  const liste = Array.isArray(doc?.paiements) ? doc.paiements : [];
  return liste
    .map((p: any) => ({ date: String(p?.datePaiement ?? ''), montant: Number(p?.montant) || 0 }))
    .filter((r: Reglement) => r.montant !== 0);
}

/**
 * Ce qu'une facture a ENCAISSÉ, et quand.
 *
 * Les règlements enregistrés font foi s'il y en a. Sinon, « Payée » vaut un
 * règlement du solde à la date de paiement ; un avoir « Payée » (remboursé)
 * vaut une sortie. Le solde est net des avoirs liés : c'est ce qui permet à
 * FACTURÉ − ENCAISSÉ = RESTE de tenir.
 */
export function reglementsDe(doc: any, solde: number): Reglement[] {
  if (!entreDansLActivite(doc)) return [];
  const enregistres = reglementsEnregistres(doc);
  if (enregistres.length > 0) return enregistres;
  if (doc?.statut !== 'Payée') return [];
  const montant = estAvoir(doc) ? -(Number(doc.totalTTC) || 0) : solde;
  return montant !== 0 ? [{ date: String(doc.datePaiement ?? ''), montant }] : [];
}

export function encaisseDe(doc: any, solde: number): number {
  return arrondir(reglementsDe(doc, solde).reduce((s, r) => s + r.montant, 0));
}

/** Ce qu'il reste dû : le solde moins l'encaissé, jamais négatif. */
export function resteDe(doc: any, solde: number): number {
  if (estAvoir(doc) || !entreDansLActivite(doc)) return 0;
  if (doc?.statut === 'Brouillon' || doc?.statut === 'Annulée') return 0;
  return arrondir(Math.max(0, solde - encaisseDe(doc, solde)));
}

/** Une facture VALIDÉE : émise, ou réglée. Ni brouillon, ni annulée, ni avoir. */
export function estValidee(doc: any): boolean {
  if (estAvoir(doc) || !entreDansLActivite(doc)) return false;
  return estEmise(doc) || doc?.statut === 'Payée';
}

export function estBrouillon(doc: any): boolean {
  return !estAvoir(doc) && entreDansLActivite(doc) && doc?.statut === 'Brouillon';
}

export interface LigneMensuelle {
  /** « 2026-08 ». */
  cle: string;
  caFactureHT: number;
  tva: number;
  caFacture: number;
  encaisse: number;
  resteAEncaisser: number;
  enRetard: number;
  brouillons: number;
  nbValidees: number;
  nbPayees: number;
  nbNonPayees: number;
  /** Parmi les non payées, celles dont l'échéance est dépassée. */
  nbEnRetard: number;
  nbBrouillons: number;
  /** Les devis émis dans le mois, TTC — indépendants du facturé et de l'encaissé. */
  devis: number;
}

export interface SyntheseFacturation {
  caFacture: number;
  caFactureHT: number;
  tva: number;
  nbValidees: number;
  encaisse: number;
  nbPayees: number;
  resteAEncaisser: number;
  nbNonPayees: number;
  enRetard: number;
  /** Parmi les non payées, celles dont l'échéance est dépassée — une SOUS-PARTIE de « nbNonPayees ». */
  nbEnRetard: number;
  brouillons: number;
  nbBrouillons: number;
  /** Les clients ayant reçu au moins une facture validée sur la période. */
  nbClients: number;
  /**
   * Le contrôle : FACTURÉ − RESTE, c'est ce que les factures de la période
   * ont encaissé. « encaisse » ci-dessus est daté par le RÈGLEMENT, sur toutes
   * les factures : les deux ne coïncident que si aucun règlement de la période
   * ne concerne une facture d'une autre période. L'écart est affiché, jamais
   * caché.
   */
  encaisseSurFacturesDeLaPeriode: number;
  ecart: number;
}

/**
 * Le détail d'une facture, tel que l'export et le clic sur un mois le montrent.
 * Une ligne = une facture, avec tout ce qu'il faut pour retrouver chaque
 * montant du tableau de bord.
 */
export interface DetailFacture {
  id: string;
  numero: string;
  client: string;
  clientId: string;
  dateCreation: string | null;
  dateFacture: string;
  dateEcheance: string | null;
  datePaiement: string | null;
  montantHT: number;
  tva: number;
  montantTTC: number;
  encaisse: number;
  soldeRestant: number;
  statut: string;
  avoirs: string;
  montantAvoirs: number;
  retardJours: number;
  moisFacturation: string;
  moisEncaissement: string | null;
  brouillon: boolean;
}

function joursDeRetard(doc: any, reste: number, maintenant: Date): number {
  if (reste <= 0 || !doc?.echeance) return 0;
  const echeance = new Date(doc.echeance);
  if (Number.isNaN(echeance.getTime())) return 0;
  const jours = Math.floor((maintenant.getTime() - echeance.getTime()) / 86_400_000);
  return jours > 0 ? jours : 0;
}

/**
 * LES CRÉANCES — ce qu'on nous doit, TOUTES PÉRIODES confondues.
 *
 * Amine, 19 septembre 2026 : « un retard reste un retard ! ». Chez
 * Euromedmultimedia, huit factures de juin échues en juillet ne s'affichaient
 * pas sur « Ce mois » : le bloc ne regardait que les factures ÉMISES dans la
 * période. Ce qui reste dû et ce qui est en retard se lisent donc sur toutes
 * les factures de la société, quelle que soit leur date d'émission — comme
 * la liste des factures. Le facturé et l'encaissé, eux, restent datés.
 */
export interface Creances {
  resteAEncaisser: number;
  nbNonPayees: number;
  /** Une SOUS-PARTIE de « resteAEncaisser ». */
  enRetard: number;
  nbEnRetard: number;
}

export interface TableauDeBord {
  synthese: SyntheseFacturation;
  /** Toutes périodes : ce qui reste dû, et ce qui est échu, à l'instant donné. */
  creances: Creances;
  /** Les lignes des mois de la période, complètes, vides à zéro. */
  mois: LigneMensuelle[];
  /** Une ligne par facture de la période (validées et brouillons), avoirs exclus. */
  factures: DetailFacture[];
}

/**
 * Le tableau de bord, calculé à partir des documents, de la période, et de
 * l'instant (pour les retards). C'est ce que « construireRapport » range
 * dans « tableauDeBord » ; l'exposer permet de le tester tel quel.
 */
export function construireTableauDeBord(
  documents: any[],
  periode: BornesPeriode,
  maintenant: Date,
  clients?: { id: string; nom: string }[],
  devis: any[] = []
): TableauDeBord {
  const tous = (documents || []).filter(entreDansLActivite);
  const imputation = imputer(tous);
  const soldeDe = (f: any) => imputation.solde(f);
  const nomClient = (f: any) =>
    f?.clientName || clients?.find((c) => c.id === f?.clientId)?.nom || '';

  const dansPeriode = tous.filter((f) => dansLaPeriode(f?.dateEmission, periode));
  const avoirsParFacture = new Map<string, any[]>();
  for (const a of tous) {
    if (!estAvoir(a) || !a.factureOriginaleId) continue;
    avoirsParFacture.set(a.factureOriginaleId, [...(avoirsParFacture.get(a.factureOriginaleId) || []), a]);
  }

  const cleDe = (dateISO: string | undefined | null) => {
    if (!dateISO) return null;
    const date = new Date(dateISO);
    return Number.isNaN(date.getTime()) ? null : cleDuMois(date);
  };

  const lignes = new Map<string, LigneMensuelle>();
  for (const cle of moisDeLaPeriode(periode.debut, periode.fin)) {
    lignes.set(cle, {
      cle, caFactureHT: 0, tva: 0, caFacture: 0, encaisse: 0, resteAEncaisser: 0, enRetard: 0,
      brouillons: 0, nbValidees: 0, nbPayees: 0, nbNonPayees: 0, nbEnRetard: 0, nbBrouillons: 0, devis: 0,
    });
  }
  const ligne = (cle: string | null) => (cle ? lignes.get(cle) ?? null : null);

  // --- Les devis : au mois de leur émission, hors archivés. Même règle que
  //     le reste du rapport ; ils ne touchent ni le facturé ni l'encaissé.
  for (const d of devis || []) {
    if (d?.statut === 'Archivé' || d?.statut === 'Archivée') continue;
    if (!dansLaPeriode(d?.dateEmission, periode)) continue;
    const l = ligne(cleDe(d.dateEmission));
    if (l) l.devis = arrondir(l.devis + (Number(d.totalTTC) || 0));
  }

  const synthese: SyntheseFacturation = {
    caFacture: 0, caFactureHT: 0, tva: 0, nbValidees: 0, encaisse: 0, nbPayees: 0,
    resteAEncaisser: 0, nbNonPayees: 0, enRetard: 0, nbEnRetard: 0, brouillons: 0, nbBrouillons: 0,
    nbClients: 0, encaisseSurFacturesDeLaPeriode: 0, ecart: 0,
  };
  const factures: DetailFacture[] = [];
  const clientsFactures = new Set<string>();

  // --- Facturé, reste, brouillons : au mois de l'ÉMISSION, sur la période.
  for (const f of dansPeriode) {
    if (estAvoir(f)) continue;
    const solde = soldeDe(f);
    const encaisse = encaisseDe(f, solde);
    const reste = resteDe(f, solde);
    const l = ligne(cleDe(f.dateEmission));
    const avoirs = avoirsParFacture.get(f.id) || [];
    const retard = joursDeRetard(f, reste, maintenant);

    if (estValidee(f)) {
      // Le HT et la TVA suivent la même proportion que le solde TTC : une
      // facture à moitié avoirée compte pour la moitié de son HT.
      const ratio = (Number(f.totalTTC) || 0) > 0 ? solde / (Number(f.totalTTC) || 1) : 0;
      const ht = arrondir((Number(f.totalHT) || 0) * ratio);
      const tva = arrondir(solde - ht);
      synthese.caFacture = arrondir(synthese.caFacture + solde);
      synthese.caFactureHT = arrondir(synthese.caFactureHT + ht);
      synthese.tva = arrondir(synthese.tva + tva);
      synthese.resteAEncaisser = arrondir(synthese.resteAEncaisser + reste);
      synthese.encaisseSurFacturesDeLaPeriode = arrondir(synthese.encaisseSurFacturesDeLaPeriode + encaisse);
      if (solde > 0) synthese.nbValidees += 1;
      if (solde > 0 && f.clientId) clientsFactures.add(String(f.clientId));
      if (reste > 0) synthese.nbNonPayees += 1;
      if (reste > 0 && retard > 0) {
        synthese.enRetard = arrondir(synthese.enRetard + reste);
        synthese.nbEnRetard += 1;
      }
      if (l) {
        l.caFacture = arrondir(l.caFacture + solde);
        l.caFactureHT = arrondir(l.caFactureHT + ht);
        l.tva = arrondir(l.tva + tva);
        l.resteAEncaisser = arrondir(l.resteAEncaisser + reste);
        if (solde > 0) l.nbValidees += 1;
        if (reste > 0) l.nbNonPayees += 1;
        if (reste > 0 && retard > 0) { l.enRetard = arrondir(l.enRetard + reste); l.nbEnRetard += 1; }
      }
    } else if (estBrouillon(f)) {
      const ttc = Number(f.totalTTC) || 0;
      synthese.brouillons = arrondir(synthese.brouillons + ttc);
      synthese.nbBrouillons += 1;
      if (l) { l.brouillons = arrondir(l.brouillons + ttc); l.nbBrouillons += 1; }
    } else {
      continue; // « Annulée » : n'existe plus, ni dans le facturé ni dans les brouillons.
    }

    const reglements = reglementsDe(f, solde);
    factures.push({
      id: f.id,
      numero: f.numero,
      client: nomClient(f),
      clientId: f.clientId,
      dateCreation: f.createdAt ?? null,
      dateFacture: f.dateEmission,
      dateEcheance: f.echeance || null,
      datePaiement: f.datePaiement || reglements[0]?.date || null,
      montantHT: Number(f.totalHT) || 0,
      tva: arrondir((Number(f.totalTTC) || 0) - (Number(f.totalHT) || 0)),
      montantTTC: Number(f.totalTTC) || 0,
      encaisse,
      soldeRestant: reste,
      statut: estValidee(f) && solde === 0 && avoirs.length > 0 ? 'Annulée par avoir'
        : estValidee(f) && avoirs.length > 0 ? `${f.statut} · partiellement avoirée` : String(f.statut),
      avoirs: avoirs.map((a) => a.numero).join(', '),
      montantAvoirs: arrondir(avoirs.reduce((s, a) => s + (Number(a.totalTTC) || 0), 0)),
      retardJours: retard,
      moisFacturation: cleDe(f.dateEmission) ?? '',
      moisEncaissement: encaisse > 0 ? cleDe(reglements[reglements.length - 1]?.date) : null,
      brouillon: estBrouillon(f),
    });
  }

  // --- Encaissé : au mois du RÈGLEMENT, sur TOUS les documents. Une facture
  //     émise avant la période mais réglée dedans compte.
  const payeesDeLaPeriode = new Set<string>();
  for (const d of tous) {
    for (const r of reglementsDe(d, soldeDe(d))) {
      if (!dansLaPeriode(r.date, periode)) continue;
      synthese.encaisse = arrondir(synthese.encaisse + r.montant);
      if (!estAvoir(d) && r.montant > 0) payeesDeLaPeriode.add(d.id);
      const l = ligne(cleDe(r.date));
      if (l) l.encaisse = arrondir(l.encaisse + r.montant);
    }
  }
  synthese.nbPayees = payeesDeLaPeriode.size;
  for (const l of lignes.values()) {
    l.nbPayees = tous.filter((d) => !estAvoir(d) && reglementsDe(d, soldeDe(d)).some((r) => r.montant > 0 && cleDe(r.date) === l.cle)).length;
  }

  synthese.nbClients = clientsFactures.size;
  synthese.ecart = arrondir(synthese.caFacture - synthese.resteAEncaisser - synthese.encaisse);

  // --- Les créances : sur TOUS les documents, sans période.
  const creances: Creances = { resteAEncaisser: 0, nbNonPayees: 0, enRetard: 0, nbEnRetard: 0 };
  for (const f of tous) {
    if (estAvoir(f) || !estValidee(f)) continue;
    const reste = resteDe(f, soldeDe(f));
    if (reste <= 0) continue;
    creances.resteAEncaisser = arrondir(creances.resteAEncaisser + reste);
    creances.nbNonPayees += 1;
    if (joursDeRetard(f, reste, maintenant) > 0) {
      creances.enRetard = arrondir(creances.enRetard + reste);
      creances.nbEnRetard += 1;
    }
  }

  return {
    synthese,
    creances,
    mois: [...lignes.values()].sort((a, b) => a.cle.localeCompare(b.cle)),
    factures: factures.sort((a, b) => a.dateFacture.localeCompare(b.dateFacture) || a.numero.localeCompare(b.numero)),
  };
}

/**
 * LES MEILLEURS MOIS — classés par CA FACTURÉ, du plus grand au plus petit.
 *
 * Amine, 19 septembre 2026 : l'onglet classait les mois par ce qu'ils avaient
 * ENCAISSÉ (février affichait 5 424 €, la facture de janvier réglée en
 * février). Le classement se fait sur le facturé au mois de la facture ; les
 * mois à zéro suivent, dans l'ordre du calendrier. Même lignes que le
 * tableau de bord : un seul calcul.
 */
export function meilleursMois(lignes: LigneMensuelle[]): LigneMensuelle[] {
  return [...lignes].sort((a, b) => (b.caFacture - a.caFacture) || a.cle.localeCompare(b.cle));
}

/**
 * Ce qu'un mois contient, pour le clic sur une ligne du tableau : les factures
 * ÉMISES pendant le mois, et les PAIEMENTS REÇUS pendant le mois — deux
 * listes, parce qu'un règlement d'août peut solder une facture de juillet.
 */
export function detailDuMois(
  documents: any[],
  cle: string,
  maintenant: Date,
  clients?: { id: string; nom: string }[]
): { emises: DetailFacture[]; paiementsRecus: DetailFacture[] } {
  const [annee, mois] = cle.split('-').map(Number);
  const bornes: BornesPeriode = {
    debut: new Date(annee, mois - 1, 1, 0, 0, 0, 0),
    fin: new Date(annee, mois, 0, 23, 59, 59, 999),
  };
  const duMois = construireTableauDeBord(documents, bornes, maintenant, clients);

  // Les factures réglées dans le mois, quelle que soit leur date d'émission :
  // on repart de la période totale et on filtre sur le mois d'encaissement.
  const total = construireTableauDeBord(
    documents, { debut: new Date(2000, 0, 1), fin: new Date(2100, 0, 1) }, maintenant, clients
  );
  const paiementsRecus = total.factures.filter((f) => f.moisEncaissement === cle && f.encaisse > 0);

  return { emises: duMois.factures, paiementsRecus };
}

// ---------------------------------------------------------------------------
// LA SITUATION DE FACTURATION — le bloc du haut du tableau de bord.
//
// Amine, 19 septembre 2026 : « en moins de 2 secondes, l'utilisateur doit
// pouvoir répondre à : combien ai-je facturé, encaissé, reste-t-il à
// encaisser, combien est en retard ? ». Les quatre nombres sont ceux de la
// synthèse ci-dessus — le même calcul que la page Rapports, sans exception.
// Ce qui suit ne fait que les RANGER pour l'écran.
// ---------------------------------------------------------------------------

export interface PartRepartition {
  nb: number;
  montant: number;
}

/**
 * La répartition des factures en trois parts qui ne se recouvrent PAS —
 * c'est ce que le graphique dessine, et une facture n'y figure qu'une fois.
 *
 * « Payées sur la période » se date par le RÈGLEMENT, comme la tuile
 * « Encaissé » juste au-dessus : mêmes factures, même montant. Amine, 19
 * septembre 2026, période du 1er août au 19 septembre : la 20231268, émise
 * en juillet et réglée le 31 août pour 24 144 €, était encaissée en haut et
 * absente en bas (0 facture, 0 €).
 *
 * « À encaisser » et « en retard » sont les CRÉANCES, toutes périodes : ce
 * qu'on nous doit ne dépend pas du mois affiché. « En retard » est une
 * sous-partie de « à encaisser », et se retire donc de la part « non échue ».
 */
export interface RepartitionFactures {
  /** Les factures réglées DANS la période, datées par le règlement — égal à « Encaissé ». */
  payees: PartRepartition;
  aEncaisserNonEchues: PartRepartition;
  enRetard: PartRepartition;
  /** Le total « à encaisser », retard COMPRIS — pour la lecture « dont en retard ». */
  aEncaisser: PartRepartition;
  nbReglees: number;
  /** Réglées dans la période + encore dues : le total du centre du graphique. */
  nbTotal: number;
  /** Les réglées sur ce total, en pour cent, entier. 0 quand il n'y a rien. */
  tauxReglees: number;
}

export function repartitionDesFactures(s: SyntheseFacturation, c: Creances): RepartitionFactures {
  const nbReglees = s.nbPayees;
  const nbTotal = nbReglees + c.nbNonPayees;
  const aEncaisserNonEchues = {
    nb: Math.max(0, c.nbNonPayees - c.nbEnRetard),
    montant: arrondir(Math.max(0, c.resteAEncaisser - c.enRetard)),
  };
  return {
    payees: { nb: nbReglees, montant: s.encaisse },
    aEncaisserNonEchues,
    enRetard: { nb: c.nbEnRetard, montant: c.enRetard },
    aEncaisser: { nb: c.nbNonPayees, montant: c.resteAEncaisser },
    nbReglees,
    nbTotal,
    tauxReglees: nbTotal > 0 ? Math.round((nbReglees / nbTotal) * 100) : 0,
  };
}

/**
 * L'encaissé rapporté au facturé, en pour cent — jamais au-delà de 100.
 *
 * « Encaissé » est daté par le RÈGLEMENT et compte les factures d'avant la
 * période réglées dedans ; « facturé » est daté par l'ÉMISSION. Le rapport
 * peut donc dépasser 100 — un mois où l'on encaisse un arriéré. On le plafonne
 * pour ne pas afficher « 140 % encaissé », et « null » quand rien n'a été
 * facturé : un pourcentage de zéro ne veut rien dire.
 */
export function tauxEncaisse(s: SyntheseFacturation): number | null {
  if (s.caFacture <= 0) return null;
  return Math.min(100, Math.round((s.encaisse / s.caFacture) * 100));
}

export type TonMessage = 'a-jour' | 'en-attente' | 'retard' | 'vide';

export interface MessageEtat {
  ton: TonMessage;
  titre: string;
  detail: string;
}

/**
 * Le message sous l'état des factures. Il dépend des DONNÉES, et de rien
 * d'autre : « Tout est à jour » n'apparaît jamais tant qu'une facture reste
 * due, échue ou non — quelle que soit la période affichée.
 */
export function messageEtat(s: SyntheseFacturation, c: Creances): MessageEtat {
  const euros = (n: number) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  const factures = (n: number) => `${n} facture${n > 1 ? 's' : ''}`;
  if (c.nbEnRetard > 0) {
    return {
      ton: 'retard',
      titre: 'Attention aux échéances',
      detail: `${euros(c.enRetard)} sont en retard sur ${factures(c.nbEnRetard)}.`,
    };
  }
  if (c.nbNonPayees > 0) {
    return {
      ton: 'en-attente',
      titre: 'Paiements en attente',
      detail: `${euros(c.resteAEncaisser)} restent à encaisser sur ${factures(c.nbNonPayees)}. Aucune échéance dépassée.`,
    };
  }
  // Rien de dû. Rien d'émis NI de réglé dans la période non plus : il n'y a
  // rien à dire. Un mois sans facture mais avec un règlement n'est pas vide.
  if (s.nbValidees === 0 && s.nbPayees === 0) {
    return {
      ton: 'vide',
      titre: 'Aucune facture sur cette période',
      detail: s.nbBrouillons > 0 ? `${factures(s.nbBrouillons)} en brouillon, non comptée${s.nbBrouillons > 1 ? 's' : ''}.` : '',
    };
  }
  return { ton: 'a-jour', titre: 'Tout est à jour !', detail: 'Aucune facture en attente de paiement.' };
}

/**
 * LES DEVIS DE LA PÉRIODE, par issue. Même périmètre que « nombreDevis » et
 * que la colonne « Devis émis » des rapports : émis dans la période, hors
 * archivés — les brouillons y sont, rangés à part.
 */
export interface SyntheseDevis {
  emis: PartRepartition;
  acceptes: PartRepartition;
  enAttente: PartRepartition;
  refuses: PartRepartition;
  brouillons: PartRepartition;
}

const DEVIS_ACCEPTES = new Set(['Accepté', 'Facturé', 'Converti', 'Signé']);
const DEVIS_EN_ATTENTE = new Set(['Envoyé', 'Envoyée', 'Téléchargé']);
const DEVIS_REFUSES = new Set(['Refusé', 'Expiré', 'Perdu']);

export function syntheseDevis(devis: any[], periode: BornesPeriode): SyntheseDevis {
  const part = (): PartRepartition => ({ nb: 0, montant: 0 });
  const r: SyntheseDevis = { emis: part(), acceptes: part(), enAttente: part(), refuses: part(), brouillons: part() };
  const ajouter = (p: PartRepartition, montant: number) => { p.nb += 1; p.montant = arrondir(p.montant + montant); };
  for (const d of devis || []) {
    if (d?.statut === 'Archivé' || d?.statut === 'Archivée') continue;
    if (!dansLaPeriode(d?.dateEmission, periode)) continue;
    const montant = Number(d.totalTTC) || 0;
    ajouter(r.emis, montant);
    const statut = String(d.statut ?? '');
    if (DEVIS_ACCEPTES.has(statut)) ajouter(r.acceptes, montant);
    else if (DEVIS_EN_ATTENTE.has(statut)) ajouter(r.enAttente, montant);
    else if (DEVIS_REFUSES.has(statut)) ajouter(r.refuses, montant);
    else ajouter(r.brouillons, montant);
  }
  return r;
}
